import classNames from 'classnames'
import { forwardRef, useCallback, useEffect, useRef } from 'react'

import { getFilterMenuContainerElement } from 'hooks/filter_menu_container_registry'
import { useFilterMenuEscapeKeydown } from 'hooks/use_filter_menu_escape_keydown'

import {
  getFilterMenuContentElement,
  setFilterMenuContentElement,
} from './filter_menu_content_registry'
import { FilterMenuContentShell } from './filter_menu_content_shell'
import { useFilterMenu } from './filter_menu_context'
import { getFilterMenuPortalRoots } from './filter_menu_portals'
import { schedulingFilterSulWidthCss } from './filter_menu_styles'

/**
 * Shared filter panel: CUI FilterMenuContent (seam bridge + shell) with registry ref for dismiss guard.
 *
 * Pass `footer` separately from body `children` so the footer stays outside the scroll region.
 */
export const SchedulingFilterMenuContent = forwardRef(({
  children,
  footer,
  className,
  contentTestId = 'scheduling-filter-menu-content',
  onKeyDown,
  ...props
}, ref) => {
  const { contentMinWidth, isOpen, onOpenChange } = useFilterMenu()
  const contentSurfaceRef = useRef(null)
  const setContentSurfaceRef = useCallback((node) => {
    contentSurfaceRef.current = node
    setFilterMenuContentElement(node)
  }, [])

  const getPortalRoots = useCallback(() => getFilterMenuPortalRoots(), [])
  const getMenuContentElement = useCallback(() => getFilterMenuContentElement(), [])

  const closePanel = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const shouldDeferFilterMenuEscapeClose = useFilterMenuEscapeKeydown({
    isOpen,
    onClosePanel: closePanel,
    getContainerElement: getFilterMenuContainerElement,
    getPortalRoots,
    getMenuContentElement,
  })

  useEffect(() => () => {
    if (contentSurfaceRef.current === getFilterMenuContentElement()) {
      setFilterMenuContentElement(null)
    }
  }, [])

  const handleKeyDown = useCallback((event) => {
    onKeyDown?.(event)

    if (event.key !== 'Escape' || event.defaultPrevented) return

    const isComposing = event.nativeEvent?.isComposing ?? event.isComposing
    if (isComposing || shouldDeferFilterMenuEscapeClose()) {
      event.preventDefault()
      event.stopPropagation()
    }
  }, [onKeyDown, shouldDeferFilterMenuEscapeClose])

  return (
    <FilterMenuContentShell
      {...props}
      ref={ref}
      contentSurfaceRef={setContentSurfaceRef}
      onKeyDown={handleKeyDown}
      data-testid={contentTestId}
      body={children}
      footer={footer}
      className={classNames(
        { [schedulingFilterSulWidthCss]: contentMinWidth === 'sul' },
        className,
      )}
    />
  )
})

/** @deprecated Use {@link SchedulingFilterMenuContent}. */
export const MeetingsFilterMenuContent = SchedulingFilterMenuContent
